import * as React from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";

import * as client from "../../../client";
import { readableLayer } from "./util";
import { useGateTrackingBase, useAalPremiumGateTracking } from "../data";
import { GeographicGateBars, AalPremiumGateSections } from "./gateViews";
import styles from "./GateExposureDialog.module.css";

type GateExposureDialogProps = {
  reportId: string;
  layer: client.Layer | null;
  onHide: () => void;
};

type Section = {
  family: "wind" | "eq";
  title: string;
  shown: boolean;
};

export const GateExposureDialog: React.FC<GateExposureDialogProps> = ({
  reportId,
  layer,
  onHide,
}) => {
  const visible = layer != null;
  const {
    data: result,
    isLoading: geoLoading,
    isError: geoError,
  } = useGateTrackingBase(reportId, layer?.layer_id ?? "", visible);
  const {
    data: perilData,
    isLoading: perilLoading,
    isError: perilError,
  } = useAalPremiumGateTracking(reportId, layer?.layer_id ?? "", visible);
  const isLoading = geoLoading || perilLoading;
  const isError = geoError || perilError;

  const sections: Section[] = [
    {
      family: "wind",
      title: "Wind / Named Storm",
      shown: !!result?.wind_shown,
    },
    { family: "eq", title: "Earthquake", shown: !!result?.eq_shown },
  ];

  return (
    <Dialog
      header={
        layer != null
          ? `Gate accumulation — ${readableLayer(layer)}`
          : "Gate accumulation"
      }
      visible={visible}
      onHide={onHide}
      style={{ width: "640px" }}
    >
      <div className={styles.container}>
        {isLoading && (
          <div className={styles.loading}>
            <ProgressSpinner />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="Could not load gate accumulation for this layer."
          />
        )}
        {!isLoading && !isError && (
          <>
            {result && (
              <>
                <Tooltip
                  target="[data-gate-current]"
                  position="top"
                  style={{ maxWidth: "360px" }}
                />
                <p className={styles.intro}>
                  Each bar is the gate's full tracking base. Filled segments
                  show bound and quoted deals across the portfolio plus this
                  layer's own contribution; the yellow and red ticks mark the
                  70% and 85% escalation breakpoints. Hover the orange segment
                  for how this layer's exposure is derived.
                </p>
                {sections.map((section) => {
                  if (!section.shown) {
                    return null;
                  }
                  const bars = result.bars.filter(
                    (b) => b.peril_family === section.family
                  );
                  return (
                    <div key={section.family} className={styles.section}>
                      <div className={styles.sectionTitle}>{section.title}</div>
                      {bars.length === 0 ? (
                        <div className={styles.emptyNote}>
                          No scheduled locations or portfolio exposure in a{" "}
                          {section.family === "wind" ? "wind" : "earthquake"}{" "}
                          gate.
                        </div>
                      ) : (
                        <GeographicGateBars
                          bars={bars}
                          breakpoints={result.breakpoints}
                        />
                      )}
                    </div>
                  );
                })}
                {!result.wind_shown && !result.eq_shown && (
                  <div className={styles.emptyNote}>
                    Neither named storm nor earthquake is enabled for this
                    account, so there are no catastrophe gates to show.
                  </div>
                )}
              </>
            )}
            {perilData && (
              <AalPremiumGateSections
                bars={perilData.bars}
                capacityBreakpoints={perilData.capacity_breakpoints}
                perLayer={true}
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
